import React, { useEffect, useState, useCallback } from 'react';
import { useTranslations } from '../../localization/useTranslations';
import { List } from '../../newsletter/types/Newsletter';
import AccountBar from '../../components/account-bar/AccountBar';
import CustomSnackbar from '../../components/CustomSnackbar';
import RegularNewslettersSection from '../components/RegularNewslettersSection';
import PremiumNewslettersSection from '../components/PremiumNewslettersSection';
import { subscribeToNewsletter, unsubscribeFromNewsletter } from '../../newsletter/services/NewsletterService';
import { fetchNewsletters, fetchSubscribedNewsletters } from '../services/NewsletterPageService';
import PremiumNewsletterModal from '../components/PremiumNewsletterModal';
import NewsletterCarousel from './NewsletterCarousel';
import useScrollbackAfterRedirect from '../../hooks/useScrollbackAfterRedirect';
import { usePremiumStatus } from '../../newsletter-landing/helpers/CheckPremiumStatus';
import { handlePendingAction } from '../../newsletter-landing/helpers/handlePendingAction';

const NewsletterPage = () => {
  useScrollbackAfterRedirect(); // Restore scroll position if redirected from login
  const translations = useTranslations();
  const [newsletters, setNewsletters] = useState<List[] | null>(null);
  const [premiumNewsletters, setPremiumNewsletters] = useState<List[] | null>(null);
  const [subscribedNewsletters, setSubscribedNewsletters] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const { isUserPremium, isPremiumStateLoading } = usePremiumStatus();
  const [shouldOpenPremodal, setShouldOpenPremodal] = useState(false);

  const openSnackbar = useCallback((message: string) => {
    setSnackbarMessage(message);
    setIsSnackbarOpen(true);
  }, []);

  const checkIsSubscribed = (newsletter: List) => {
    return subscribedNewsletters.includes(newsletter.code);
  };

  const handleNewsletterChange = async (newsletter: List) => {
    const isSubscribed = checkIsSubscribed(newsletter);

    try {
      if (isSubscribed) {
        const response = await unsubscribeFromNewsletter(newsletter.code);
        if (response.status === 'ok') {
          openSnackbar(
            translations?.snackBarMessages?.unsubscribedFromNewsletter || 'You have unsubscribed from this newsletter'
          );
          setSubscribedNewsletters(subscribedNewsletters.filter((code) => code !== newsletter.code));
        }
      } else {
        const response = await subscribeToNewsletter(newsletter.code);
        if (response.status === 'ok') {
          openSnackbar(
            translations?.snackBarMessages?.subscribedToNewsletter || 'You have subscribed to this newsletter'
          );
          setSubscribedNewsletters([...subscribedNewsletters, newsletter.code]);
        }
      }
    } catch (error) {
      console.error(error);
      openSnackbar(
        translations?.snackBarMessages?.somethingWentWrong || 'Something went wrong. Please try again later.'
      );
    }
  };

  const onCloseSnackbar = () => {
    setIsSnackbarOpen(false);
  };

  useEffect(() => {
    if (window.crm_user) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }

    const fetchData = async () => {
      try {
        const { regularList, premiumList } = await fetchNewsletters();
        const subscribedCodes = await fetchSubscribedNewsletters();

        setNewsletters(regularList);
        setPremiumNewsletters(premiumList);
        setSubscribedNewsletters(subscribedCodes);
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        openSnackbar(
          translations?.snackBarMessages?.somethingWentWrong || 'Something went wrong. Please try again later.'
        );
      }
    };

    fetchData();
  }, [openSnackbar, translations]);

  // useEffect hook to handle pending actions and snackbar message
  useEffect(() => {
    // Retrieve the snackbar message type from session storage
    const snackbarMessageType = localStorage.getItem('snackbarMessage');

    // Function to get the message based on the message type stored before login
    const getMessage = () => {
      switch (snackbarMessageType) {
        case 'newsletter-success':
          return translations?.snackBarMessages?.subscribedToNewsletter || 'You have subscribed to this newsletter';
        case 'newsletter-error':
          return translations?.snackBarMessages?.failedSubscribeAuthor || 'Error subscribing to the newsletter';
        case 'follow-author-success':
          return translations?.snackBarMessages?.subscribedAuthor || 'Author followed successfully!';
        case 'follow-author-error':
          return translations?.snackBarMessages?.failedSubscribeAuthor || 'Error following author. Please try again.';
        case 'follow-topic-success':
          return translations?.snackBarMessages?.subscribedTopic || 'Topic followed successfully!';
        case 'follow-topic-error':
          return translations?.snackBarMessages?.failedSubscribeTopic || 'Error following topic. Please try again.';
        default:
          return '';
      }
    };

    // If a snackbar message is found, display it and remove it from local storage
    if (snackbarMessageType) {
      openSnackbar(getMessage());
      localStorage.removeItem('snackbarMessage');
    }
  }, [isPremiumStateLoading, isUserPremium, openSnackbar, translations]);

  useEffect(() => {
    if (isPremiumStateLoading) return;

    handlePendingAction(
      isUserPremium ?? false,
      openSnackbar,
      setSubscribedNewsletters,
      setShouldOpenPremodal,
      translations
    );
  }, [isPremiumStateLoading, isUserPremium, openSnackbar, translations]);

  useEffect(() => {
    setIsDialogOpen(shouldOpenPremodal ?? false);
  }, [shouldOpenPremodal]);

  if (isLoading) {
    return (
      <main className="page-newsletter__content" style={{ height: '100vh' }}>
        <div className="container" />
      </main>
    );
  }

  const modalContent = {
    ...window.premiumModalFields,
    header_background_image:
      typeof window.premiumModalFields?.header_background_image === 'string'
        ? window.premiumModalFields?.header_background_image
        : undefined,
  };

  return (
    <div className="container">
      <CustomSnackbar message={snackbarMessage} open={isSnackbarOpen} onClose={onCloseSnackbar} />
      <div className="page-newsletter__content">
        {isLoggedIn && (
          <div className="page-newsletter__account-bar">
            <AccountBar />
          </div>
        )}

        {/* Regular Newsletters Section */}
        <RegularNewslettersSection
          newsletters={newsletters}
          onNewsletterChange={handleNewsletterChange}
          checkIsSubscribed={checkIsSubscribed}
        />

        {/* Premium Newsletters Section */}
        {premiumNewsletters && premiumNewsletters.length > 0 && (
          <PremiumNewslettersSection
            premiumNewsletters={premiumNewsletters}
            handlePremiumLinkClick={() => setIsDialogOpen(true)}
            onNewsletterChange={handleNewsletterChange}
            checkIsSubscribed={checkIsSubscribed}
            premiumLink={modalContent?.button_link}
          />
        )}

        <NewsletterCarousel cardType="author" openSnackbar={openSnackbar} />
        <NewsletterCarousel cardType="topic" openSnackbar={openSnackbar} />
      </div>

      {/* Premium Newsletter Modal */}
      {modalContent && (
        <PremiumNewsletterModal
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          modalContent={modalContent}
          shouldOpenPremodal={shouldOpenPremodal}
        />
      )}
    </div>
  );
};

export default NewsletterPage;
